import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Calendar, CheckCircle, Package, Activity, Plus, Briefcase, Edit, Trash2, Filter, User, Users, Search, Check, FileText, Bot, X, DollarSign } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Modal from '../components/common/Modal';
import NotificationSystem, { useNotification } from '../components/common/NotificationSystem';
import CustomSelect from '../components/common/CustomSelect';
import projectService from '../services/projectService';
import userService from '../services/userService';
import { useAuth } from '../context/AuthContext';
import { ROLES } from '../config/permissions';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('es-CO', { year: 'numeric', month: 'short', day: 'numeric' }) : 'No definida';
const fmtDateTime = (d) => {
  if (!d) return 'No definida';
  const dateObj = new Date(d);
  return `${dateObj.toLocaleDateString('es-CO', { year: 'numeric', month: 'short', day: 'numeric' })} ${dateObj.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}`;
};

const statusColors = {
  // Fases
  PENDING: { bg: '#f3f4f6', text: '#6b7280', label: 'Pendiente' },
  WORKING: { bg: '#dbeafe', text: '#2563eb', label: 'Trabajando' },
  COMPLETED: { bg: '#dcfce7', text: '#16a34a', label: 'Completada' },
  DELAYED: { bg: '#fef2f2', text: '#dc2626', label: '⚠ Retrasada' },
  // Proyecto
  PLANNING: { bg: '#f3f4f6', text: '#6b7280' },
  IN_PROGRESS: { bg: '#dbeafe', text: '#2563eb' },
  ON_HOLD: { bg: '#fff7ed', text: '#ea580c' },
  DELIVERED: { bg: '#dcfce7', text: '#16a34a' },
  CANCELLED: { bg: '#fef2f2', text: '#dc2626' }
};

const statusLabels = {
  PLANNING: 'Planificación',
  IN_PROGRESS: 'En Progreso',
  ON_HOLD: 'En Pausa',
  DELIVERED: 'Entregado',
  CANCELLED: 'Cancelado'
};

const ProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { notification, showNotification, hideNotification } = useNotification();

  const [project, setProject] = useState(null);
  const [phases, setPhases] = useState([]);
  const [activities, setActivities] = useState([]);
  const [activityFilter, setActivityFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const { user } = useAuth();
  
  const isManagerOrAdmin = user && [ROLES.SUPER_ADMIN, ROLES.GERENTE, ROLES.DIRECTOR_DESARROLLO].includes(user.role);
  const isDev = user && user.role === ROLES.ASESOR_DESARROLLO;
  const canSeeFinancialData = user && !isDev;

  // Modal registrar avance
  const [showLogModal, setShowLogModal] = useState(false);
  const [logType, setLogType] = useState('WORK'); // 'WORK' o 'COMMENT'
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [logHours, setLogHours] = useState('');
  const [showTechnicalDocModal, setShowTechnicalDocModal] = useState(false);
  const [showSettlementModal, setShowSettlementModal] = useState(false);
  
  // Estados para Modal de Cambio de Estado
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [submittingStatus, setSubmittingStatus] = useState(false);
  const [logDescription, setLogDescription] = useState('');
  const [submittingLog, setSubmittingLog] = useState(false);

  // Modals edición
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [projectForm, setProjectForm] = useState({ name: '', description: '' });

  const [showPhaseModal, setShowPhaseModal] = useState(false);
  const [isEditingPhase, setIsEditingPhase] = useState(false);
  const [phaseForm, setPhaseForm] = useState({ title: '', description: '', estimatedHours: 0, complexity: 'MEDIUM' });

  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [assignmentSearchTerm, setAssignmentSearchTerm] = useState('');
  const [selectedUserForAssignment, setSelectedUserForAssignment] = useState(null);

  // Estados Documento Técnico
  const [generatingTechnicalDoc, setGeneratingTechnicalDoc] = useState(false);

  useEffect(() => { loadProject(); }, [id]);

  const loadProject = async (isSilent = false) => {
    try {
      if (!isSilent) setLoading(true);
      const data = await projectService.getProjectById(id);
      setProject(data.project);
      setPhases(data.phases || []);

      const actData = await projectService.getProjectActivities(id, activityFilter);
      setActivities(actData.activities || []);

      if (isManagerOrAdmin && !isSilent) {
        const resUsers = await userService.getUsers();
        setUsers(resUsers.users || []);
      }
    } catch (error) {
      if (!isSilent) showNotification('Error al cargar el proyecto', 'error');
    } finally { if (!isSilent) setLoading(false); }
  };

  useEffect(() => {
    if (project) loadProject(true); // Carga silenciosa para filtros
  }, [activityFilter]);

  const handleUpdateAssignedTo = async (userId) => {
    try {
      await projectService.updateProject(id, { assignedTo: userId || null });
      showNotification('Desarrollador asignado con éxito', 'success');
      loadProject();
    } catch (error) {
      showNotification(error.message, 'error');
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus) return;
    if (['ON_HOLD', 'CANCELLED'].includes(newStatus) && !statusReason.trim()) {
      return showNotification('Debes proporcionar un motivo para este cambio de estado', 'error');
    }

    try {
      setSubmittingStatus(true);
      await projectService.updateProject(id, { 
        status: newStatus,
        statusReason: statusReason.trim() 
      });
      showNotification('Estado del proyecto actualizado', 'success');
      setShowStatusModal(false);
      setStatusReason('');
      loadProject();
    } catch (error) {
      showNotification(error.message, 'error');
    } finally {
      setSubmittingStatus(false);
    }
  };

  const isLocked = ['DELIVERED', 'CANCELLED', 'ON_HOLD'].includes(project?.status);

  const handleLogWork = async () => {
    if (!logDescription.trim() || !selectedPhase) return;
    if (logType === 'WORK' && (!logHours || isNaN(parseFloat(logHours)))) return;
    
    try {
      setSubmittingLog(true);
      if (logType === 'WORK') {
        await projectService.logPhaseWork(selectedPhase._id, {
          hours: parseFloat(logHours),
          description: logDescription
        });
        showNotification('✅ Avance registrado exitosamente', 'success');
      } else {
        await projectService.addPhaseObservation(selectedPhase._id, logDescription);
        showNotification('✅ Comentario agregado exitosamente', 'success');
      }
      
      setLogHours('');
      setLogDescription('');
      setShowLogModal(false);
      loadProject();
    } catch (error) {
      showNotification(error.message, 'error');
    } finally {
      setSubmittingLog(false);
    }
  };

  const handleEditProject = async (e) => {
    e.preventDefault();
    try {
      await projectService.updateProject(id, projectForm);
      showNotification('Proyecto actualizado', 'success');
      setShowEditProjectModal(false);
      loadProject();
    } catch (error) { showNotification(error.message, 'error'); }
  };

  const handleSavePhase = async (e) => {
    e.preventDefault();
    try {
      if (isEditingPhase) {
        await projectService.updatePhase(selectedPhase._id, phaseForm);
        showNotification('Fase actualizada', 'success');
      } else {
        await projectService.createPhase(id, phaseForm);
        showNotification('Fase creada', 'success');
      }
      setShowPhaseModal(false);
      loadProject();
    } catch (error) { showNotification(error.message, 'error');    }
  };

  const handleGenerateTechnicalDoc = async () => {
    try {
      setGeneratingTechnicalDoc(true);
      const data = await projectService.generateTechnicalDocument(id);
      setProject({ ...project, technicalDocument: data.technicalDocument });
      showNotification('✅ Documento Técnico generado exitosamente', 'success');
      setShowTechnicalDocModal(true);
    } catch (error) {
      showNotification(error.message, 'error');
    } finally {
      setGeneratingTechnicalDoc(false);
    }
  };

  const handleDeletePhase = async (phaseId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta fase? Esta acción es irreversible pero quedará registrada en el historial.')) return;
    try {
      await projectService.deletePhase(phaseId);
      showNotification('Fase eliminada correctamente', 'success');
      loadProject();
    } catch (error) {
      showNotification(error.message, 'error');
    }
  };

  const openEditProjectModal = () => {
    setProjectForm({ name: project.name, description: project.description || '' });
    setShowEditProjectModal(true);
  };

  const openAddPhaseModal = () => {
    setIsEditingPhase(false);
    setPhaseForm({ title: '', description: '', estimatedHours: 0, complexity: 'MEDIUM' });
    setShowPhaseModal(true);
  };

  const openEditPhaseModal = (phase) => {
    setIsEditingPhase(true);
    setSelectedPhase(phase);
    setPhaseForm({
      title: phase.title,
      description: phase.description || '',
      estimatedHours: phase.estimatedHours,
      complexity: phase.complexity
    });
    setShowPhaseModal(true);
  };

  if (loading) {
    return <div className="flex flex-col items-center justify-center py-12"><div className="loading-spinner"></div><p className="mt-4 text-gray-600">Cargando proyecto...</p></div>;
  }

  if (!project) {
    return <div className="text-center py-12"><p>Proyecto no encontrado</p></div>;
  }

  // Recopilar todo el timeline global combinando históricos de fases y la nueva colección Activity
  let globalTimeline = [];
  
  // Agregar las nuevas actividades desde la bbdd central (PRIORIDAD)
  activities.forEach(act => {
     globalTimeline.push({
       id: act._id,
       type: act.type === 'WORK_LOG' ? 'WORK' : 'SYS',
       description: act.description,
       hours: act.hours,
       phaseName: act.phaseName || '',
       phaseNumber: act.phaseNumber || '',
       dateObj: new Date(act.createdAt),
       user: act.user || null
     });
  });

  // Solo agregar registros manuales (legados) si no están en la colección central para evitar duplicados
  phases.forEach(p => {
    if (!activityFilter || activityFilter === p._id) {
      if (p.workLogs) {
        p.workLogs.forEach(log => {
          // Si el log ya está en activities (por horas y fecha aproximada), no lo agregamos
          const logDate = new Date(log.date);
          const isAlreadyInActivities = activities.some(act => 
            act.type === 'WORK_LOG' && 
            Math.abs(new Date(act.createdAt) - logDate) < 60000 && // 1 minuto de margen
            act.hours === log.hours
          );

          if (!isAlreadyInActivities) {
            globalTimeline.push({ 
              id: log._id,
              ...log, 
              type: 'WORK', 
              phaseName: p.title, 
              phaseNumber: p.number, 
              dateObj: logDate,
              user: log.createdBy ? { isLegacy: true } : null
            });
          }
        });
      }
      if (p.observations) {
        p.observations.forEach(obs => {
          const obsDate = new Date(obs.createdAt);
          const isAlreadyInActivities = activities.some(act => 
            act.type === 'OBSERVATION' && 
            Math.abs(new Date(act.createdAt) - obsDate) < 60000
          );

          if (!isAlreadyInActivities) {
            globalTimeline.push({ 
              id: obs._id,
              ...obs, 
              type: 'OBS', 
              description: obs.content, 
              phaseName: p.title, 
              phaseNumber: p.number, 
              dateObj: obsDate 
            });
          }
        });
      }
    }
  });

  globalTimeline.sort((a, b) => b.dateObj - a.dateObj);


  return (
    <div className="animate-fadeIn" style={{ paddingBottom: '40px' }}>
      {/* Header flotante */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <button className="btn btn-secondary btn-icon" onClick={() => navigate('/projects')}>
          <ArrowLeft size={20} /> Volver a Proyectos
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {isManagerOrAdmin && (
            <button 
              onClick={() => isManagerOrAdmin && !isLocked && setShowAssignmentModal(true)}
              disabled={isLocked || !isManagerOrAdmin}
              className="btn btn-outline btn-sm"
              style={{ 
                display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', 
                backgroundColor: 'white', cursor: (isLocked || !isManagerOrAdmin) ? 'not-allowed' : 'pointer',
                opacity: (isLocked || !isManagerOrAdmin) ? 0.6 : 1
              }}
            >
              <User size={18} style={{ color: '#6B7280' }} />
              <div style={{ textAlign: 'left' }}>
                <span style={{ display: 'block', fontSize: '0.7rem', color: '#9CA3AF', textTransform: 'uppercase', fontWeight: 600 }}>Asignado a:</span>
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>
                  {project?.assignedTo ? (
                    project.assignedTo.fullName || 
                    (project.assignedTo.personal?.firstName ? `${project.assignedTo.personal.firstName} ${project.assignedTo.personal.lastName || ''}`.trim() : project.assignedTo.email)
                  ) : '-- Sin Asignar --'}
                </span>
              </div>
            </button>
          )}

          <span style={{ fontSize: '0.9rem', color: '#6B7280', fontWeight: '500', marginLeft: '12px' }}>Estado:</span>
          <button 
            onClick={() => {
              if (isManagerOrAdmin) {
                setNewStatus(project.status);
                setShowStatusModal(true);
              }
            }}
            style={{ 
              padding: '6px 16px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: '700',
              backgroundColor: statusColors[project.status]?.bg || '#F3F4F6',
              color: statusColors[project.status]?.text || '#6B7280',
              border: `1px solid ${statusColors[project.status]?.text}33`,
              cursor: isManagerOrAdmin ? 'pointer' : 'default',
              display: 'flex', alignItems: 'center', gap: '8px'
            }}
          >
            {statusLabels[project.status] || project.status}
            {isManagerOrAdmin && <Edit size={14} />}
          </button>
        </div>
      </div>

      {/* Alerta de Documento Técnico (si es dev y no hay documento) */}
      {isDev && !project.technicalDocument && (
        <div style={{ backgroundColor: '#DBEAFE', border: '1px solid #3B82F6', borderRadius: '12px', padding: '16px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Bot size={24} style={{ color: '#2563EB' }} />
            <div>
              <p style={{ margin: 0, fontWeight: '700', color: '#1E40AF' }}>¿Listo para empezar a desarrollar?</p>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#3B82F6' }}>Genera la guía técnica para entender la arquitectura y lógica del proyecto.</p>
            </div>
          </div>
          <button 
            onClick={handleGenerateTechnicalDoc}
            disabled={generatingTechnicalDoc}
            className="btn btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            {generatingTechnicalDoc ? <div className="loading-spinner-small"></div> : <Bot size={18} />}
            {generatingTechnicalDoc ? 'Generando...' : 'Generar Guía Técnica'}
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        
          {/* ====== COLUMNA IZQUIERDA (2/3) ====== */}
        <div style={{ flex: '1 1 60%', display: 'flex', flexDirection: 'column', gap: '20px', minWidth: '300px' }}>
          
          <div className="card">
            <div className="card-header" style={{ padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-1">{project.name}</h1>
                <p className="text-sm text-gray-500">Panel de ejecución y gestión técnica</p>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {isManagerOrAdmin && (
                  <button onClick={openEditProjectModal} className="btn btn-outline btn-sm" title="Editar Proyecto" style={{ border: '1px solid #E5E7EB', color: '#6B7280' }}>
                    <Edit size={16} />
                  </button>
                )}
              </div>
            </div>

            {/* Bloque de Documento Técnico IA - Ubicación Centralizada */}
            <div style={{ padding: '0 24px 24px', marginTop: '-8px' }}>
              <div style={{ 
                backgroundColor: project.technicalDocument ? '#F0F9FF' : '#F9FAFB', 
                border: `1px solid ${project.technicalDocument ? '#BAE6FD' : '#E5E7EB'}`,
                borderRadius: '12px', padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ backgroundColor: project.technicalDocument ? '#E0F2FE' : '#F3F4F6', padding: '8px', borderRadius: '10px' }}>
                    <FileText size={20} style={{ color: project.technicalDocument ? '#0284C7' : '#6B7280' }} />
                  </div>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#111827' }}>Documento Técnico de Desarrollo</h4>
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#6B7280' }}>
                      {project.technicalDocument ? 'Guía de arquitectura y lógica lista para el equipo.' : 'Análisis inteligente de requerimientos para programadores.'}
                    </p>
                  </div>
                </div>
                
                <button 
                  onClick={project.technicalDocument ? () => setShowTechnicalDocModal(true) : handleGenerateTechnicalDoc}
                  disabled={generatingTechnicalDoc}
                  className={project.technicalDocument ? "btn btn-outline btn-sm" : "btn btn-primary btn-sm"}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: '160px', justifyContent: 'center' }}
                >
                  {generatingTechnicalDoc ? (
                    <div className="loading-spinner-small"></div>
                  ) : (
                    project.technicalDocument ? <FileText size={16} /> : <Bot size={16} />
                  )}
                  {generatingTechnicalDoc ? 'Procesando...' : (project.technicalDocument ? 'Ver Documento' : 'Generar Documento')}
                </button>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
            <h2 className="text-xl font-bold text-gray-900" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Activity size={24} style={{ color: '#059669' }} /> Fases del Proyecto
            </h2>
            {isManagerOrAdmin && !isLocked && (
              <button onClick={openAddPhaseModal} className="btn btn-outline btn-sm" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Plus size={16} /> Nueva Fase
              </button>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxHeight: '520px', overflowY: 'auto', paddingRight: '4px' }}>
            {phases.length === 0 ? (
              <div className="card"><div className="card-content text-center text-gray-500 py-8">No hay fases definidas</div></div>
            ) : (
              phases.map(phase => {
                const statusInfo = statusColors[phase.status] || statusColors.PENDING;
                return (
                  <div key={phase._id} className="card" style={{ 
                    borderLeft: `4px solid ${statusInfo.text}`, 
                    backgroundColor: phase.status === 'DELAYED' ? '#FEF2F2' : 'white',
                    position: 'relative'
                  }}>
                    {isManagerOrAdmin && !isLocked && (
                      <div style={{ position: 'absolute', top: '16px', right: '16px', display: 'flex', gap: '8px' }}>
                        <button 
                          onClick={() => handleDeletePhase(phase._id)} 
                          style={{ color: '#F87171', background: 'none', border: 'none', cursor: 'pointer' }}
                          title="Eliminar Fase"
                        >
                          <Trash2 size={16} />
                        </button>
                        <button 
                          onClick={() => openEditPhaseModal(phase)} 
                          style={{ color: '#9CA3AF', background: 'none', border: 'none', cursor: 'pointer' }}
                          title="Editar Fase"
                        >
                          <Edit size={16} />
                        </button>
                      </div>
                    )}
                    
                    <div className="card-content" style={{ padding: '20px', paddingRight: '70px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                            <span style={{ backgroundColor: '#F3F4F6', color: '#4B5563', padding: '2px 8px', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.85rem' }}>
                              Fase {phase.number}
                            </span>
                            <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#111827', margin: 0 }}>{phase.title}</h3>
                            <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 700, backgroundColor: statusInfo.bg, color: statusInfo.text }}>
                              {statusInfo.label}
                            </span>
                            <span className={`badge ${phase.complexity === 'HIGH' ? 'badge-secondary' : phase.complexity === 'LOW' ? 'badge-success' : 'badge-primary'}`} style={{ fontSize: '10px' }}>
                              {phase.complexity}
                            </span>
                          </div>
                          {phase.description && <p style={{ color: '#6B7280', fontSize: '0.9rem', margin: 0 }}>{phase.description}</p>}
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '20px', marginBottom: '16px', fontSize: '0.85rem', color: '#4B5563', backgroundColor: '#F9FAFB', padding: '10px 14px', borderRadius: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Clock size={16} style={{ color: '#6B7280' }}/>
                          <span>Horas Reales: <strong>{phase.actualHours || 0}h</strong> / {phase.estimatedHours}h estimadas</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Calendar size={16} style={{ color: '#6B7280' }}/>
                          <span>Planificado: <strong>{fmtDate(phase.startDate)}</strong> → <strong>{fmtDate(phase.endDate)}</strong></span>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#374151', minWidth: '65px' }}>Progreso:</span>
                        <div style={{ flex: 1, backgroundColor: '#E5E7EB', height: '12px', borderRadius: '6px', overflow: 'hidden' }}>
                          <div style={{ 
                            width: `${phase.progressPercentage}%`, height: '100%', 
                            background: phase.progressPercentage >= 100 ? 'linear-gradient(90deg, #10B981, #059669)' : 'linear-gradient(90deg, #3B82F6, #2563EB)', 
                            transition: 'width 0.5s ease' 
                          }}></div>
                        </div>
                        <span style={{ fontSize: '0.95rem', fontWeight: '700', color: phase.progressPercentage >= 100 ? '#059669' : '#111827', minWidth: '45px', textAlign: 'right' }}>
                          {phase.progressPercentage}%
                        </span>
                        
                        {!isLocked && phase.status !== 'COMPLETED' && (
                          <button 
                            onClick={() => { setSelectedPhase(phase); setShowLogModal(true); }}
                            className="btn btn-primary btn-sm ml-4"
                            style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600' }}
                          >
                            <Plus size={16} /> Registrar Avance
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '24px', marginBottom: '8px' }}>
            <h2 className="text-xl font-bold text-gray-900" style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
              <Clock size={24} style={{ color: '#8B5CF6' }} /> Historial de Actividad
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Filter size={16} style={{ color: '#6B7280' }} />
              <select 
                value={activityFilter}
                onChange={(e) => setActivityFilter(e.target.value)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #D1D5DB',
                  fontSize: '0.85rem',
                  outline: 'none',
                  backgroundColor: 'white'
                }}
              >
                <option value="">Todas las Fases</option>
                {phases.map(p => (
                  <option key={p._id} value={p._id}>Fase {p.number}: {p.title}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="card">
            <div className="card-content" style={{ padding: '20px', minHeight: '300px' }}>
              {globalTimeline.length === 0 ? (
                <p className="text-center text-gray-500 py-4">No hay actividad registrada aún.</p>
              ) : (
                <div style={{ 
                  position: 'relative', borderLeft: '2px solid #E5E7EB', marginLeft: '12px', paddingLeft: '20px', 
                  display: 'flex', flexDirection: 'column', gap: '20px',
                  maxHeight: '600px', overflowY: 'auto', paddingRight: '10px'
                }}>
                  {globalTimeline.map((item, idx) => (
                    <div key={idx} style={{ position: 'relative' }}>
                      <div style={{ 
                        position: 'absolute', left: '-29px', top: '4px', width: '16px', height: '16px', 
                        borderRadius: '50%', backgroundColor: item.type === 'WORK' ? '#D1FAE5' : item.type === 'SYS' ? '#E0E7FF' : '#FEF3C7', 
                        border: `2px solid ${item.type === 'WORK' ? '#10B981' : item.type === 'SYS' ? '#3B82F6' : '#F59E0B'}` 
                      }}></div>
                      <div style={{ fontSize: '0.8rem', color: '#6B7280', marginBottom: '4px' }}>
                        {fmtDateTime(item.dateObj)} 
                        {item.phaseNumber && ` • Fase ${item.phaseNumber}: ${item.phaseName}`}
                        {item.user && (
                          <span style={{ marginLeft: '8px', color: '#4B5563', fontWeight: '500' }}>
                            👤 {item.user.isLegacy ? 'Usuario Legado' : (
                              item.user.fullName || 
                              (item.user.personal?.firstName ? `${item.user.personal.firstName} ${item.user.personal.lastName || ''}`.trim() : item.user.email)
                            )}
                          </span>
                        )}
                      </div>
                      <div style={{ backgroundColor: '#F9FAFB', padding: '12px 16px', borderRadius: '8px', border: '1px solid #F3F4F6' }}>
                        {item.type === 'WORK' && (
                          <span style={{ display: 'inline-block', backgroundColor: '#DBEAFE', color: '#1D4ED8', padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 'bold', marginBottom: '6px' }}>
                            ⏱ {item.hours}h trabajadas
                          </span>
                        )}
                        <p style={{ margin: 0, color: '#374151', fontSize: '0.95rem' }}>{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ====== COLUMNA DERECHA (1/3) ====== */}
        <div style={{ flex: '1 1 320px', display: 'flex', flexDirection: 'column', gap: '20px', minWidth: '300px', maxWidth: '420px' }}>
          
          <div className="card" style={{ background: 'linear-gradient(135deg, #065F46 0%, #047857 50%, #059669 100%)', color: 'white', border: 'none' }}>
            <div className="card-content" style={{ padding: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <div style={{ backgroundColor: 'rgba(255,255,255,0.2)', padding: '12px', borderRadius: '12px' }}>
                  <Briefcase size={28} color="white" />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: '#D1FAE5' }}>Progreso del Proyecto</h3>
                  <div style={{ fontSize: '2rem', fontWeight: '800', lineHeight: 1, marginTop: '4px' }}>
                    {project.overallProgress || 0}%
                  </div>
                </div>
              </div>
              
              <div style={{ backgroundColor: 'rgba(0,0,0,0.15)', height: '14px', borderRadius: '7px', overflow: 'hidden', marginBottom: '24px' }}>
                <div style={{ width: `${project.overallProgress || 0}%`, height: '100%', backgroundColor: '#34D399', borderRadius: '7px', transition: 'width 0.5s' }} />
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '16px', borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: '16px' }}>
                <div>
                  <div style={{ fontSize: '0.85rem', color: '#A7F3D0' }}>Horas Totales</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: '700' }}>{project.totalActualHours || 0}h / {project.totalEstimatedHours || 0}h</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.85rem', color: '#A7F3D0' }}>Fases Completas</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: '700' }}>{phases.filter(p => p.status === 'COMPLETED').length} / {phases.length}</div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                <span style={{ color: '#D1FAE5' }}>Tasa de avance:</span>
                <span style={{ fontWeight: '600' }}>
                  {project.totalEstimatedHours > 0 
                    ? Math.round(((project.totalActualHours || 0) / project.totalEstimatedHours) * 100) 
                    : 0}% del tiempo presupuestado
                </span>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Calendar size={20} style={{ color: '#8B5CF6' }} /> Cronograma</h3></div>
            <div className="card-content" style={{ padding: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6B7280', fontSize: '0.9rem' }}>Fecha de Inicio:</span>
                  <span style={{ fontWeight: '600', fontSize: '0.9rem' }}>{fmtDate(project.startDate)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6B7280', fontSize: '0.9rem' }}>Entrega Estimada:</span>
                  <span style={{ fontWeight: '600', fontSize: '0.9rem' }}>{fmtDate(project.estimatedEndDate)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '12px', borderTop: '1px solid #E5E7EB' }}>
                  <span style={{ color: '#6B7280', fontSize: '0.9rem' }}>Entrega Real:</span>
                  <span style={{ fontWeight: '700', fontSize: '0.9rem', color: project.actualEndDate ? '#059669' : '#9CA3AF' }}>
                    {project.actualEndDate ? fmtDate(project.actualEndDate) : 'Pendiente'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* === LIQUIDACIÓN DESARROLLADOR === */}
          {(isManagerOrAdmin || isDev) && (
          <div className="card" style={{ border: '1px solid #E0E7FF' }}>
            <div className="card-header" style={{ background: 'linear-gradient(135deg, #EEF2FF, #E0E7FF)', borderBottom: '1px solid #C7D2FE' }}>
              <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#3730A3' }}>
                <DollarSign size={20} style={{ color: '#4F46E5' }} /> Liquidación Desarrollador
              </h3>
            </div>
            <div className="card-content" style={{ padding: '16px' }}>
              {(() => {
                const saleTotal = project.sale?.total || 0;
                const settlement = project.developerSettlement || { percentage: 30, totalAmount: 0, payments: [] };
                const devTotal = settlement.totalAmount || Math.round(saleTotal * (settlement.percentage || 30) / 100);
                const paidAmount = (settlement.payments || []).reduce((sum, p) => sum + p.amount, 0);
                const remaining = devTotal - paidAmount;
                const progressPct = devTotal > 0 ? Math.min(100, Math.round((paidAmount / devTotal) * 100)) : 0;

                return (
                  <>
                    {/* Resumen financiero */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: '#6B7280', textTransform: 'uppercase', fontWeight: 600 }}>Valor Proyecto</div>
                        <div style={{ fontSize: '1.2rem', fontWeight: '800', color: '#4F46E5' }}>${devTotal.toLocaleString()}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '0.7rem', color: '#6B7280', textTransform: 'uppercase', fontWeight: 600 }}>Saldo Restante</div>
                        <div style={{ fontSize: '1.2rem', fontWeight: '800', color: remaining > 0 ? '#DC2626' : '#059669' }}>${remaining.toLocaleString()}</div>
                      </div>
                    </div>

                    {/* Barra de progreso */}
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: '4px' }}>
                        <span style={{ color: '#6B7280' }}>Pagado: ${paidAmount.toLocaleString()}</span>
                        <span style={{ fontWeight: '700', color: progressPct >= 100 ? '#059669' : '#4F46E5' }}>{progressPct}%</span>
                      </div>
                      <div style={{ backgroundColor: '#E5E7EB', height: '10px', borderRadius: '5px', overflow: 'hidden' }}>
                        <div style={{ 
                          width: `${progressPct}%`, height: '100%', 
                          background: progressPct >= 100 
                            ? 'linear-gradient(90deg, #059669, #10B981)' 
                            : 'linear-gradient(90deg, #4F46E5, #6366F1)',
                          borderRadius: '5px', transition: 'width 0.5s ease' 
                        }} />
                      </div>
                    </div>

                    {/* Botón Registrar Abono */}
                    {isManagerOrAdmin && settlement.totalAmount > 0 && remaining > 0 && (
                      <button 
                        onClick={() => setShowSettlementModal(true)}
                        className="btn btn-primary btn-sm" 
                        style={{ 
                          width: '100%', marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                        }}
                      >
                        <DollarSign size={16} /> Registrar Abono
                      </button>
                    )}

                    {/* Si no hay total Amount definido */}
                    {isManagerOrAdmin && (!settlement.totalAmount && saleTotal > 0) && (
                      <button 
                        onClick={async () => {
                          try {
                            const calcTotal = Math.round(saleTotal * 30 / 100);
                            await projectService.updateSettlementConfig(id, { totalAmount: calcTotal, percentage: 30 });
                            loadProject();
                            showNotification(`Liquidación generada: $${calcTotal.toLocaleString()} (30%)`, 'success');
                          } catch (e) { showNotification(e.message, 'error'); }
                        }}
                        className="btn btn-outline btn-sm" 
                        style={{ 
                          width: '100%', marginBottom: '12px', borderColor: '#C7D2FE', color: '#4F46E5'
                        }}
                      >
                        ⚙️ Generar Liquidación
                      </button>
                    )}

                    {remaining <= 0 && settlement.totalAmount > 0 && (
                      <div style={{ backgroundColor: '#D1FAE5', padding: '10px', borderRadius: '10px', textAlign: 'center', marginBottom: '12px', border: '1px solid #6EE7B7' }}>
                        <span style={{ color: '#065F46', fontWeight: '700', fontSize: '0.85rem' }}>✅ Liquidación completada</span>
                      </div>
                    )}

                    {/* Historial de abonos (SCROLLABLE) */}
                    {(settlement.payments || []).length > 0 && (
                      <div>
                        <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#374151', marginBottom: '8px', textTransform: 'uppercase' }}>
                          Historial de Abonos ({settlement.payments.length})
                        </div>
                        <div style={{ maxHeight: '160px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px', paddingRight: '4px' }}>
                          {[...settlement.payments].reverse().map((p, idx) => (
                            <div key={idx} style={{ 
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                              padding: '8px 10px', backgroundColor: '#F9FAFB', borderRadius: '8px', border: '1px solid #F3F4F6'
                            }}>
                              <div>
                                <div style={{ fontWeight: '700', fontSize: '0.85rem', color: '#059669' }}>+${p.amount?.toLocaleString()}</div>
                                <div style={{ fontSize: '0.7rem', color: '#6B7280', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {p.notes || (p.method === 'TRANSFER' ? 'Transferencia' : p.method === 'CASH' ? 'Efectivo' : 'Abono')}
                                </div>
                              </div>
                              <div style={{ fontSize: '0.65rem', color: '#9CA3AF', textAlign: 'right' }}>
                                {new Date(p.date).toLocaleDateString()}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
          )}

        </div>
      </div>

      {/* ====== MODAL REGISTRAR AVANCE ====== */}
      {showLogModal && (
        <div 
          onClick={(e) => { if (e.target === e.currentTarget) setShowLogModal(false); }}
          style={{ 
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
            backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', 
            zIndex: 9999, padding: '20px' 
          }}
        >
          <div style={{ 
            backgroundColor: 'white', borderRadius: '16px', width: '100%', maxWidth: '520px',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)', animation: 'fadeIn 0.2s ease-out',
            display: 'flex', flexDirection: 'column', maxHeight: '90vh'
          }}>
            <div style={{ 
              padding: '20px 24px', borderBottom: '1px solid #E5E7EB',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: 'linear-gradient(135deg, #1E40AF, #2563EB)', borderRadius: '16px 16px 0 0', color: 'white'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Clock size={22} />
                <span style={{ fontSize: '1.1rem', fontWeight: '700' }}>{logType === 'WORK' ? 'Registrar Avance' : 'Agregar Comentario'}</span>
              </div>
              <button 
                onClick={() => {
                  setShowLogModal(false);
                  setLogType('WORK');
                }} 
                style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1.4rem', padding: '0 4px', lineHeight: 1 }}
              >✕</button>
            </div>

            <div style={{ padding: '0 24px', paddingTop: '16px' }}>
               <div style={{ display: 'flex', gap: '4px', backgroundColor: '#F3F4F6', padding: '4px', borderRadius: '10px' }}>
                 <button 
                    onClick={() => setLogType('WORK')}
                    style={{ 
                      flex: 1, padding: '8px', borderRadius: '8px', border: 'none', fontSize: '0.85rem', fontWeight: '600',
                      backgroundColor: logType === 'WORK' ? 'white' : 'transparent',
                      color: logType === 'WORK' ? '#1E40AF' : '#6B7280',
                      boxShadow: logType === 'WORK' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                      cursor: 'pointer', transition: 'all 0.2s'
                    }}
                 >
                   Horas Trabajadas
                 </button>
                 <button 
                    onClick={() => setLogType('COMMENT')}
                    style={{ 
                      flex: 1, padding: '8px', borderRadius: '8px', border: 'none', fontSize: '0.85rem', fontWeight: '600',
                      backgroundColor: logType === 'COMMENT' ? 'white' : 'transparent',
                      color: logType === 'COMMENT' ? '#1E40AF' : '#6B7280',
                      boxShadow: logType === 'COMMENT' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                      cursor: 'pointer', transition: 'all 0.2s'
                    }}
                 >
                   Comentario / Obs.
                 </button>
               </div>
            </div>

            <div style={{ padding: '24px', paddingTop: '16px', overflowY: 'auto' }}>
              <div style={{ marginBottom: '20px', padding: '12px 16px', backgroundColor: '#F9FAFB', borderRadius: '8px', border: '1px solid #F3F4F6' }}>
                <strong style={{ display: 'block', color: '#111827', marginBottom: '4px' }}>Fase {selectedPhase?.number}: {selectedPhase?.title}</strong>
                <span style={{ fontSize: '0.8rem', color: '#6B7280' }}>
                  {logType === 'WORK' 
                    ? `Llevas ${selectedPhase?.actualHours || 0}h de ${selectedPhase?.estimatedHours || 0}h estimadas.`
                    : 'Agrega una nota u observación sobre esta fase.'}
                </span>
              </div>

              {logType === 'WORK' && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontWeight: '600', fontSize: '0.85rem', color: '#374151', marginBottom: '6px' }}>Horas Trabajadas Hoy *</label>
                  <input
                    type="number" step="0.5" min="0.5"
                    value={logHours}
                    onChange={e => setLogHours(e.target.value)}
                    placeholder="Ej: 2.5"
                    className="form-input"
                    style={{ fontSize: '1.2rem', fontWeight: '700', height: '48px' }}
                    autoFocus
                  />
                </div>
              )}

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontWeight: '600', fontSize: '0.85rem', color: '#374151', marginBottom: '6px' }}>
                  {logType === 'WORK' ? '¿Qué hiciste? (Bitácora) *' : 'Observación / Comentario *'}
                </label>
                <textarea
                  value={logDescription}
                  onChange={e => setLogDescription(e.target.value)}
                  placeholder={logType === 'WORK' ? "Describe el trabajo realizado..." : "Escribe tu comentario u observación aquí..."}
                  className="form-input form-textarea"
                  rows="4"
                />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button 
                  onClick={() => {
                    setShowLogModal(false);
                    setLogType('WORK');
                  }} 
                  className="btn btn-outline" style={{ flex: 1, height: '48px', fontWeight: '600' }}
                >Cancelar</button>
                <button
                  onClick={handleLogWork}
                  disabled={submittingLog || (logType === 'WORK' && !logHours) || !logDescription.trim()}
                  className="btn btn-primary"
                  style={{ flex: 2, height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontWeight: '700' }}
                >
                  {submittingLog ? 'Registrando...' : <><CheckCircle size={20} /> {logType === 'WORK' ? 'Guardar Avance' : 'Guardar Comentario'}</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL EDITAR PROYECTO */}
      <Modal isOpen={showEditProjectModal} onClose={() => setShowEditProjectModal(false)} title="Editar Proyecto" onSubmit={handleEditProject} submitText="Guardar Cambios">
        <div className="form-group">
          <label className="form-label">Nombre del Proyecto</label>
          <input type="text" value={projectForm.name} onChange={e => setProjectForm({ ...projectForm, name: e.target.value })} className="form-input" required />
        </div>
        <div className="form-group mt-3">
          <label className="form-label">Descripción</label>
          <textarea value={projectForm.description} onChange={e => setProjectForm({ ...projectForm, description: e.target.value })} className="form-input form-textarea" rows="3" />
        </div>
      </Modal>

      {/* MODAL CREAR / EDITAR FASE */}
      <Modal isOpen={showPhaseModal} onClose={() => setShowPhaseModal(false)} title={isEditingPhase ? "Editar Fase" : "Nueva Fase"} onSubmit={handleSavePhase} submitText="Guardar Fase">
        <div className="form-group">
          <label className="form-label">Título de la Fase</label>
          <input type="text" value={phaseForm.title} onChange={e => setPhaseForm({ ...phaseForm, title: e.target.value })} className="form-input" required />
        </div>
        <div className="form-group mt-3">
          <label className="form-label">Descripción</label>
          <textarea value={phaseForm.description} onChange={e => setPhaseForm({ ...phaseForm, description: e.target.value })} className="form-input form-textarea" rows="2" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '12px' }}>
          <div className="form-group">
            <label className="form-label">Horas Estimadas</label>
            <input type="number" min="0" value={phaseForm.estimatedHours} onChange={e => setPhaseForm({ ...phaseForm, estimatedHours: parseInt(e.target.value) || 0 })} className="form-input" required />
          </div>
          <CustomSelect
            label="Complejidad"
            value={phaseForm.complexity}
            onChange={v => setPhaseForm({ ...phaseForm, complexity: v })}
            options={[
              { value: 'LOW', label: 'Baja' },
              { value: 'MEDIUM', label: 'Media' },
              { value: 'HIGH', label: 'Alta' }
            ]}
          />
        </div>
      </Modal>

      {/* MODAL ASIGNACIÓN PROFESIONAL */}
      <Modal 
        isOpen={showAssignmentModal} 
        onClose={() => {
          setShowAssignmentModal(false);
          setSelectedUserForAssignment(null);
          setAssignmentSearchTerm('');
        }} 
        title="Asignar Responsable"
        onSubmit={(e) => {
          e.preventDefault();
          if (selectedUserForAssignment) {
            handleUpdateAssignedTo(selectedUserForAssignment._id);
            setShowAssignmentModal(false);
            setSelectedUserForAssignment(null);
            setAssignmentSearchTerm('');
          }
        }}
        submitText={selectedUserForAssignment ? `Confirmar Asignación a ${(selectedUserForAssignment.fullName || selectedUserForAssignment.name || '').split(' ')[0]}` : "Selecciona un usuario"}
        disableSubmit={!selectedUserForAssignment}
      >
        <div style={{ marginBottom: '16px' }}>
          <div style={{ position: 'relative' }}>
            <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
            <input 
              type="text" 
              placeholder="Buscar por nombre o rol..."
              value={assignmentSearchTerm}
              onChange={(e) => setAssignmentSearchTerm(e.target.value)}
              className="form-input"
              style={{ paddingLeft: '40px' }}
              autoFocus
            />
          </div>
        </div>

        <div style={{ maxHeight: '350px', overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
           {users
            .filter(u => {
              const userName = (u.fullName || u.name || '').toLowerCase();
              const userRole = (u.roleLabel || u.role || '').toLowerCase();
              const searchTerm = (assignmentSearchTerm || '').toLowerCase();
              return userName.includes(searchTerm) || userRole.includes(searchTerm);
            })
            .map(u => (
              <div 
                key={u._id}
                onClick={() => setSelectedUserForAssignment(u)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #F3F4F6',
                  backgroundColor: selectedUserForAssignment?._id === u._id ? '#EFF6FF' : 'transparent',
                  transition: 'background-color 0.2s'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                   <div style={{ 
                     width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#F3F4F6',
                     display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: '#6B7280'
                   }}>
                     {(u.fullName || u.name || u.email || '?').charAt(0).toUpperCase()}
                   </div>
                   <div>
                     <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#374151' }}>{u.fullName || u.name || u.email}</div>
                     <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>{u.role || 'Sin Rol'}</div>
                   </div>
                </div>
                {selectedUserForAssignment?._id === u._id && (
                  <Check size={20} style={{ color: '#3B82F6' }} />
                )}
              </div>
            ))
          }
          {users.filter(u => {
              const userName = (u.fullName || u.name || '').toLowerCase();
              const userRole = (u.roleLabel || u.role || '').toLowerCase();
              const searchTerm = (assignmentSearchTerm || '').toLowerCase();
              return userName.includes(searchTerm) || userRole.includes(searchTerm);
          }).length === 0 && (
            <div style={{ padding: '24px', textAlign: 'center', color: '#9CA3AF' }}>
              <Users size={32} style={{ margin: '0 auto 8px', opacity: 0.5 }} />
              <p>No se encontraron usuarios</p>
            </div>
          )}
        </div>
        
        <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
           <button 
             type="button"
             onClick={() => {
               if (window.confirm('¿Deseas quitar la asignación actual del proyecto?')) {
                 handleUpdateAssignedTo(null);
                 setShowAssignmentModal(false);
               }
             }}
             className="btn btn-sm"
             style={{ width: '100%', color: '#EF4444', backgroundColor: '#FEF2F2', border: 'none' }}
           >
             Quitar Asignación Actual
           </button>
        </div>
      </Modal>

      {/* MODAL DOCUMENTO TÉCNICO IA */}
      {showTechnicalDocModal && (
        <div 
          onClick={(e) => { if (e.target === e.currentTarget) setShowTechnicalDocModal(false); }}
          style={{ 
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
            backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', 
            zIndex: 9999, padding: '20px' 
          }}
        >
          <div style={{ 
            backgroundColor: 'white', borderRadius: '24px', width: '100%', maxWidth: '900px', height: '90vh',
            boxShadow: '0 25px 50px rgba(0,0,0,0.3)', animation: 'slideUp 0.3s ease-out',
            display: 'flex', flexDirection: 'column', overflow: 'hidden'
          }}>
            <div style={{ 
              padding: '24px 32px', borderBottom: '1px solid #F3F4F6',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: 'linear-gradient(135deg, #0F172A, #1E293B)', color: 'white'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div style={{ backgroundColor: 'rgba(56, 189, 248, 0.2)', padding: '10px', borderRadius: '12px' }}>
                  <Bot size={24} style={{ color: '#38BDF8' }} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '800' }}>Guía Técnica de Desarrollo</h3>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: '#94A3B8' }}>Generado automáticamente por Xtreme-AI</p>
                </div>
              </div>
              <button 
                onClick={() => setShowTechnicalDocModal(false)} 
                style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', cursor: 'pointer', padding: '8px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: '32px', overflowY: 'auto', flex: 1, backgroundColor: '#FFFFFF' }} className="markdown-body">
              <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {project.technicalDocument}
                </ReactMarkdown>
              </div>
            </div>

            <div style={{ padding: '20px 32px', borderTop: '1px solid #F3F4F6', backgroundColor: '#F9FAFB', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowTechnicalDocModal(false)} className="btn btn-primary" style={{ minWidth: '120px' }}>
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL REGISTRAR ABONO LIQUIDACIÓN */}
      {showSettlementModal && (
        <div 
          onClick={(e) => { if (e.target === e.currentTarget) setShowSettlementModal(false); }}
          style={{ 
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
            backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', 
            zIndex: 9999, padding: '20px' 
          }}
        >
          <div style={{ 
            backgroundColor: 'white', borderRadius: '16px', width: '100%', maxWidth: '440px',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)', animation: 'fadeIn 0.2s ease-out',
            overflow: 'hidden'
          }}>
            <div style={{ 
              padding: '20px 24px', borderBottom: '1px solid #E5E7EB',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: 'linear-gradient(135deg, #4F46E5, #6366F1)', color: 'white'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <DollarSign size={22} />
                <span style={{ fontSize: '1.1rem', fontWeight: '700' }}>Registrar Abono</span>
              </div>
              <button onClick={() => setShowSettlementModal(false)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1.4rem' }}>✕</button>
            </div>

            <div style={{ padding: '24px' }}>
              {(() => {
                const settlement = project.developerSettlement || { totalAmount: 0, payments: [] };
                const paidAmount = (settlement.payments || []).reduce((sum, p) => sum + p.amount, 0);
                const remaining = settlement.totalAmount - paidAmount;

                return (
                  <>
                    <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#F3F4F6', borderRadius: '10px', textAlign: 'center' }}>
                      <div style={{ fontSize: '0.8rem', color: '#6B7280', textTransform: 'uppercase', marginBottom: '2px' }}>Saldo Pendiente</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: '800', color: '#111827' }}>${remaining.toLocaleString()}</div>
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label className="form-label">Monto del Abono *</label>
                      <input 
                        type="number" id="modal-settlement-amount"
                        placeholder="Ej: 500000" min="1" max={remaining}
                        className="form-input" style={{ fontSize: '1.1rem', fontWeight: '700' }}
                        autoFocus
                      />
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label className="form-label">Método de Pago</label>
                      <select id="modal-settlement-method" className="form-input">
                        <option value="TRANSFER">Transferencia</option>
                        <option value="CASH">Efectivo</option>
                        <option value="CHECK">Cheque</option>
                        <option value="OTHER">Otro</option>
                      </select>
                    </div>

                    <div style={{ marginBottom: '24px' }}>
                      <label className="form-label">Notas / Observaciones</label>
                      <input type="text" id="modal-settlement-notes" placeholder="Opcional..." className="form-input" />
                    </div>

                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button onClick={() => setShowSettlementModal(false)} className="btn btn-outline" style={{ flex: 1 }}>Cancelar</button>
                      <button 
                        onClick={async () => {
                          const amtEl = document.getElementById('modal-settlement-amount');
                          const methodEl = document.getElementById('modal-settlement-method');
                          const notesEl = document.getElementById('modal-settlement-notes');
                          const amt = parseFloat(amtEl?.value);
                          if (!amt || amt <= 0) return showNotification('Monto inválido', 'error');
                          try {
                            await projectService.addSettlementPayment(id, { amount: amt, method: methodEl?.value, notes: notesEl?.value });
                            showNotification('✅ Abono registrado', 'success');
                            setShowSettlementModal(false);
                            loadProject();
                          } catch (e) { showNotification(e.message, 'error'); }
                        }}
                        className="btn btn-primary" style={{ flex: 1.5, fontWeight: '700' }}
                      >
                        Confirmar Pago
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* MODAL CAMBIO DE ESTADO */}
      {showStatusModal && (
        <div 
          onClick={(e) => { if (e.target === e.currentTarget) setShowStatusModal(false); }}
          style={{ 
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
            backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', 
            zIndex: 9999, padding: '20px' 
          }}
        >
          <div style={{ 
            backgroundColor: 'white', borderRadius: '16px', width: '100%', maxWidth: '440px',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)', animation: 'fadeIn 0.2s ease-out',
            overflow: 'hidden'
          }}>
            <div style={{ 
              padding: '20px 24px', borderBottom: '1px solid #E5E7EB',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: 'linear-gradient(135deg, #111827, #374151)', color: 'white'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Edit size={22} />
                <span style={{ fontSize: '1.1rem', fontWeight: '700' }}>Actualizar Estado</span>
              </div>
              <button onClick={() => setShowStatusModal(false)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1.4rem' }}>✕</button>
            </div>

            <div style={{ padding: '24px' }}>
              <div style={{ marginBottom: '20px' }}>
                <label className="form-label">Nuevo Estado del Proyecto</label>
                <div style={{ display: 'grid', gap: '8px', marginTop: '8px' }}>
                  {[
                    { value: 'PLANNING', label: 'Planificación' },
                    { value: 'IN_PROGRESS', label: 'En Progreso' },
                    { value: 'ON_HOLD', label: 'En Pausa' },
                    { value: 'DELIVERED', label: 'Entregado' },
                    { value: 'CANCELLED', label: 'Cancelado' }
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setNewStatus(opt.value)}
                      disabled={project.status === 'DELIVERED' && opt.value !== 'DELIVERED'}
                      style={{
                        padding: '12px 16px', borderRadius: '12px', textAlign: 'left',
                        border: `2px solid ${newStatus === opt.value ? statusColors[opt.value].text : '#F3F4F6'}`,
                        backgroundColor: newStatus === opt.value ? statusColors[opt.value].bg : 'white',
                        color: newStatus === opt.value ? statusColors[opt.value].text : '#4B5563',
                        fontWeight: newStatus === opt.value ? '700' : '500',
                        fontSize: '0.9rem', cursor: 'pointer', transition: 'all 0.2s',
                        opacity: project.status === 'DELIVERED' && opt.value !== 'DELIVERED' ? 0.5 : 1
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {['ON_HOLD', 'CANCELLED'].includes(newStatus) && (
                <div style={{ marginBottom: '24px', animation: 'fadeIn 0.3s ease' }}>
                  <label className="form-label" style={{ color: '#DC2626' }}>Explicación del Motivo *</label>
                  <textarea
                    value={statusReason}
                    onChange={(e) => setStatusReason(e.target.value)}
                    placeholder="Describe brevemente por qué se pausa o cancela el proyecto..."
                    className="form-input form-textarea"
                    rows="3"
                    style={{ borderColor: '#FCA5A5' }}
                    autoFocus
                  />
                  <p style={{ fontSize: '0.75rem', color: '#EF4444', marginTop: '6px' }}>Esta nota quedará registrada en el historial de actividades.</p>
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px' }}>
                <button onClick={() => setShowStatusModal(false)} className="btn btn-outline" style={{ flex: 1 }}>Volver</button>
                <button 
                  onClick={handleUpdateStatus}
                  disabled={submittingStatus || (['ON_HOLD', 'CANCELLED'].includes(newStatus) && !statusReason.trim())}
                  className="btn btn-primary" 
                  style={{ flex: 1.5, fontWeight: '700' }}
                >
                  {submittingStatus ? 'Guardando...' : 'Confirmar Cambio'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <NotificationSystem notification={notification} onClose={hideNotification} />
    </div>
  );
};

export default ProjectDetailPage;
